/**
 * NoPermissionModal
 *
 * Shown when the operator can't migrate for the selected source→target pair. When the per-instance
 * breakdown is supplied it explains WHY per side (which instance lacks which permission, or whose
 * account isn't active) — because the sidebar-level aggregate can say "you can migrate" while a
 * specific pair grants neither permission on both sides. Without a breakdown it falls back to the
 * generic "no permission anywhere" copy.
 *
 * @module components/NoPermissionModal
 */

import React from "react";
import { ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import type { InstanceCapability } from "../lib/api";

interface NoPermissionModalProps {
  open: boolean;
  onClose: () => void;
  /** Per-side breakdown for the selected pair; when present the modal explains the specific reason. */
  instances?: InstanceCapability[];
}

/** One human-readable reason a side blocks the migration, or null when that side is fine. */
function sideReason(instance: InstanceCapability): string | null {
  const where = `${instance.name} (${instance.role === "SOURCE" ? "source" : "target"})`;
  switch (instance.userStatus) {
    case "ARCHIVED":
      return `Your account is archived on ${where}.`;
    case "NOT_FOUND":
      return `No account matching your username or email exists on ${where}.`;
    case "ERROR":
      return `Your account could not be verified on ${where}.`;
    case "ACTIVE":
    default: {
      const missing: string[] = [];
      if (!instance.hasProcessPermission) missing.push("Process Migration");
      if (!instance.hasOntologyPermission) missing.push("Ontology Migration");
      if (missing.length === 0) return null; // this side grants everything — not the blocker
      return `Missing ${missing.join(" and ")} permission on ${where}.`;
    }
  }
}

export const NoPermissionModal: React.FC<NoPermissionModalProps> = ({ open, onClose, instances }) => {
  const reasons = (instances ?? []).map(sideReason).filter((r): r is string => r !== null);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-muted/60 text-foreground/80">
            <ShieldAlert className="h-5 w-5" />
          </div>
          <DialogTitle>You don't have permission to migrate</DialogTitle>
          <DialogDescription>
            {reasons.length > 0
              ? "You can't migrate between the selected instances for the following reason(s):"
              : "Your account does not have permission to perform any migrations. Please contact an administrator to be granted the Process Migration or Ontology Migration permission."}
          </DialogDescription>
        </DialogHeader>
        {reasons.length > 0 && (
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {reasons.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        )}
        {reasons.length > 0 && (
          <p className="text-sm text-muted-foreground">Contact your administrator to be granted the required permission.</p>
        )}
        <DialogFooter>
          <Button onClick={onClose}>Understood</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NoPermissionModal;
